/* global BUILD_TIMESTAMP */ // L6
// L5

// dependencies
import { camelCase, get } from "lodash-es";
import { ThemeProvider } from "styled-components";
import { wpDesignTokens } from "@pazien/component-library";

// other
import { bodyFontSize, colorLegacy, colorStyle2pt0, headerFontSize } from "common/themes/designTokens/designTokens";
import { store } from "common/store";
import { fontStyles, fontSizes } from "./font";

const isStyle2pt0 = store?.getState?.()?.settings?.isStyle2pt0; // L1

const spaces = {
    "0": "0",
    "0.25": "2px",
    "0.5": "4px",
    "0.75": "6px",
    "1": "8px",
    "1.5": "12px",
    "2": "16px",
    "3": "20px",
    "4": "30px"
};

const {
    blue3,
    blue6,
    blue11,
    brick1,
    canvas1,
    canvas4,
    gray1,
    gray4,
    gray6,
    green1,
    green4,
    keylime1,
    orange3,
    purple3,
    wcag5,
    wcag6
} = colorLegacy;

const analyticsSectionColors = {
    all_fraud: brick1.value,
    authorizations: blue3.value,
    averageTicket: orange3.value,
    cashflow: green4.value,
    chargebacks: purple3.value,
    fees: orange3.value,
    fraudsight: green1.value,
    misc: keylime1.value,
    netSettledCash: blue6.value,
    psd2_fraud: brick1.value,
    refundRate: blue6.value,
    refunds: blue6.value,
    sales: wcag6.value,
    settlements: wcag6.value,
    threedsflex: green1.value
};

const ellipsis = (maxWidth = "100%", display = "inline-block") => `
    display: ${display};
    max-width: ${maxWidth};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ellipsis2 = (maxWidth = "100%") => `
    max-width: ${maxWidth};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// From base.scss, to be main theme colors
const baseStyles = {
    // Text colors
    textColorPrimary: `${gray1.value} !default`,
    textColorPrimaryKnockout: `${canvas4.value} !default`,
    textColorSecondary: `${gray4.value} !default`,

    // Background colors
    backgroundColorPrimary: `${canvas4.value} !default`,
    backgroundColorSecondary: `${canvas1.value} !default`,

    // Branding colors
    colorCorpPrimary: `${wcag5.value} !default`,
    colorCorpSecondary: `${green4.value} !default`,

    // Misc colors
    colorActiveLink: `${green4.value} !default`,

    // Dimensions
    "dimension-topbar-border-width": "1px !default",
    "dimension-topbar-height": "50px !default",
    "page-padding": "16px !default",
    "popover-max-width": "500px !default",
    "sidebar-padding-large": "180px !default",
    "sidebar-padding-small-plus-scroll": "70px !default",
    "sidebar-padding-small": "50px !default",
    "sidebar-small-width": "50px !default",
    "sidebar-width": "180px !default",
    "sidebar-zindex": "1028 !default",
    "tab-bar-height": "39px !default",
    "top-content-bar-height": "62px !default"
};

/**
 * Gets a legacy design token which is guaranteed not to be `undefined`
 *   or `null`. This keeps the application from crashing when the toggle
 *   for `isStyle2pt0` is not enabled but the code is looking for a
 *   style 2.0 design token.
 * @param {string} colorName
 * @todo Once legacy design tokens are eliminated, this function and the
 *   lines that call it can be removed.
 * @returns {Object} The value of the design token.
 */
const getSubstitueColorValue = (colorName) => {
    if (colorName === "palette-border-quaternary") {
        return gray6;
    }
    if (colorName === "palette-brand-primary") {
        return blue11;
    }
    return colorStyle2pt0.generic;
};

/**
 * Gets the value of a single design token.
 * @param {"color" | "radius"} type
 * @param {string} tokenName for example, "blue3", "gray1"
 * @param {string} legacyValue a fallback value to use if `tokenName` is
 *   not found
 * @todo Push errors to `window.walog` when in local development mode
 *   like we do in `getDesignTokens()`.
 * @returns {string} The value of the design token.
 */
export const getDesignToken = (type, tokenName, legacyValue = "") => {
    if (type !== "color" && type !== "radius") {
        throw new Error(`Unsupported design token type: ${type}.`);
    }
    if (typeof tokenName !== "string") {
        throw new TypeError("The type for `tokenName` must be a string.");
    }
    if (type === "color") {
        if (!theme.color[tokenName] && legacyValue) {
            return legacyValue;
        } else if (!theme.color[tokenName] && !legacyValue) {
            return colorStyle2pt0.generic.value;
        }
        return theme.color[tokenName].value;
    }
    // radius
    if (theme.radius && theme.radius[tokenName]) {
        return theme.radius[tokenName].value;
    }
    // fallback for unknown radius
    return legacyValue || "0px";
};

/**
 * Returns `window` typed as `CustomWindow`, using an intermediate cast
 * to `unknown` to satisfy TS2352 when widening.
 * @todo Isolate this function in a utils file. Remember to move also
 *   the `CustomWindow` typedef found at the bottom of this file.
 * @returns {CustomWindow}
 */
const getCustomWindow = () => /** @type {CustomWindow} */(/** @type {unknown} */(window));

/**
 * Gets the values of multiple design tokens.
 * @param {"color" | "radius"} type
 * @param {string[]} tokenNames for example, `["white1", "black1"]`
 * @returns {{[key: string]: string}} An object of key/value pairs where
 *   the key names are the design token names converted to camel case
 *   and the corresponding value for each one is the design token's
 *   value expressed as a string.
 */
export const getDesignTokens = (type, tokenNames) => {
    const isLocal = process.env.NODE_ENV === "development" || window.location.hostname === "localhost";

    if (isLocal && typeof window !== "undefined") {
        getCustomWindow().walog = /** @type {any} */(window).walog || []; // L4
    }
    if (type !== "color" && type !== "radius") {
        throw new Error(`Unsupported design token type: ${type}`);
    }
    if (!Array.isArray(tokenNames) || tokenNames.length === 0) {
        throw new TypeError("`tokenNames` must be a non-empty array of strings.");
    }
    if (!tokenNames.every((t) => typeof t === "string")) {
        throw new TypeError("All token names must be strings.");
    }

    const result = tokenNames.reduce((acc, tokenName) => {
        const keyname = camelCase(tokenName);
        if (type === "color") {
            if (!theme.color[tokenName]) {
                acc[keyname] = getSubstitueColorValue(tokenName).value;
                isLocal && getCustomWindow().walog // L4
                    .push(`PAZIEN ERROR: ${tokenName} as a color is not currently supported. A substitute color is being used instead.`);
            } else {
                acc[keyname] = theme.color[tokenName].value;
            }
        }
        if (type === "radius") {
            if (theme.radius && theme.radius[tokenName]) {
                acc[keyname] = theme.radius[tokenName].value;
            } else {
                acc[keyname] = "0px";
                isLocal && getCustomWindow().walog // L4
                    .push(`PAZIEN ERROR: ${tokenName} as a radius is not currently supported. A value of 0px is being used instead.`);
            }
        }
        return acc;
    }, {});
    
    return { ...result };
};

const waTheme = { // L2
    analyticsSectionColor: (section) => get(analyticsSectionColors, section),
    backgroundColor: baseStyles.backgroundColorPrimary,
    baseStyles,
    bodyFontSize,
    borderRadius: "2px",
    color: colorLegacy,
    colorCorp: baseStyles.colorCorpPrimary,
    font: (style, size) => `font-family: ${fontStyles[style]};\nfont-size: ${fontSizes[size]};`, // Backwards compatibility with `@include font(style, size)` rule
    getDesignToken,
    getDesignTokens,
    headerFontSize,
    isStyle2pt0,
    mixins: {
        ellipsis,
        ellipsis2
    },
    radius: {},
    size: (size) => fontSizes[size], // Backwards compatibility with current typography system (h0..h7)
    space: (size) => spaces[size],
    text: { ...fontStyles },
    textColor: baseStyles.textColorPrimary
};

export const theme = isStyle2pt0
    ? {
        ...waTheme,
        baseStyles: {
            ...baseStyles,
            backgroundColorPrimary: wpDesignTokens.color["palette-border-secondary"].value // L3
        },
        bodyFontSize: wpDesignTokens.bodyFontSize,
        color: {
            ...waTheme.color,
            ...wpDesignTokens.color,
            ...colorStyle2pt0
        },
        headerFontSize: wpDesignTokens.headerFontSize,
        radius: wpDesignTokens.radius
    }
    : waTheme;

export const withTheme = (Component, selectedTheme = theme) => (
    <ThemeProvider theme={selectedTheme}>{Component}</ThemeProvider>
);

if (isStyle2pt0) {
    const wrapper = document.getElementById("wrapper"); // S1
    const activeTheme = document.getElementById("theme-stylesheet"); // S2
    const body = document.body;
    if (wrapper) {
        wrapper.style.backgroundColor = theme.baseStyles.backgroundColorPrimary;
    }
    if (body) {
        body.classList.add("ui-theme");
    }
    if (activeTheme) {
        // @ts-ignore BUILD_TIMESTAMP is injected at build time
        activeTheme.setAttribute("href", `/merchant/theme-new-${BUILD_TIMESTAMP}.css`); // S2 // S3
    }
}

/**
 * @typedef {Object} CustomWindow
 * @property {any} walog
 */

/*

NOTES

[L1]
TODO
The optional chaining we have here is primarily to ensure that Jest does
not fail because of the error "TypeError: _store.store.getState is not a
function". Once ui-theme is more fully implemented, we will need to use
a mocked store to test that values change when the `isStyle2pt0` toggle
is enabled.

[L2]
The `wa` prefix here stands for "web application" and denotes the
internal theme we have used for some time. This differs from the the
`wp` prefix which denotes the WorldPay design tokens which we began to
implement beginning in March 2025.

[L3]
TODO
The hex value of `palette-border-secondary` is the same as the hex value
for `pallette-bg-secondary`. But we are leaving this for now because
we are also using a color that is named "secondary" in a variable that
is named "primary". Once we have the official JSON file from WorldPay,
we need to clean this up. In other words, we are leaving this as is
because it's going to change regardless.

[L4]
Beginning in August 2025, we now collect these warnings in `.walog`
which can be accessed in the browser's dev tools when working locally.

TODO
Consider adding other values we want to track and make available for
developers during debugging.

[L5]
For an example of how Sentry handles themes in their application–
// https://github.com/getsentry/sentry/blob/42eda359bf9b18a02ff5dccea9763d846cda780c/src/sentry/static/sentry/app/utils/theme.tsx

[L6]
This keeps ESLint from complaining about the use of `BUILD_TIMESTAMP`
which is injected at build time.

*/
